<script>
  // The New project tile's modal (spec "Wants" > Shelf): paste a deck or
  // cube link to register it as a Project (ADR-0016). The sites a link can
  // come from are shown as their bundled icons, not listed in words; the one
  // the link belongs to lights up. A refused link (the cap, or one the
  // server cannot read) keeps the modal open with the reason. The dialog
  // itself (focus on the link field and back to the tile, Escape, backdrop)
  // is shared/Dialog.
  import Dialog from '../shared/Dialog.svelte';
  import { SOURCE_SITES, siteOfLink } from '../../lib/logic/want-page-logic.js';

  /**
   * @type {{
   *   onAdd: (link: string) => Promise<{ ok: boolean, error?: string }>,
   *   onClose: () => void,
   * }}
   */
  let { onAdd, onClose } = $props();

  let link = $state('');
  let busy = $state(false);
  let error = $state('');

  const lit = $derived(siteOfLink(link));

  /** @param {SubmitEvent} e */
  async function onSubmit(e) {
    e.preventDefault();
    const url = link.trim();
    if (!url || busy) return;
    busy = true;
    error = '';
    const res = await onAdd(url);
    busy = false;
    if (!res.ok) error = res.error ?? '';
  }
</script>

<Dialog title="New project" width={460} {onClose}>
  <form onsubmit={onSubmit}>
    <div class="sources">
      {#each SOURCE_SITES as s (s.source)}
        <span
          class="src"
          class:lit={lit === s.source}
          class:dim={lit !== null && lit !== s.source}
          title={s.name}
        >
          <img src={s.icon} alt={s.name} />
        </span>
      {/each}
    </div>
    <input
      bind:value={link}
      oninput={() => (error = '')}
      placeholder="Paste a deck or cube link"
      aria-label="Deck or cube link"
      autocomplete="off"
      data-autofocus
    />
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    <div class="actions">
      <button type="button" class="secondary" onclick={onClose}>Cancel</button>
      <button type="submit" class="primary" disabled={!link.trim() || busy}>Add project</button>
    </div>
  </form>
</Dialog>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 10px 20px 20px;
  }
  .sources {
    display: flex;
    gap: 8px;
  }
  .src {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid var(--color-border);
    border-radius: 50%;
    transition:
      opacity var(--dur-fast),
      border-color var(--dur-fast);
  }
  .src img {
    width: 22px;
    height: 22px;
  }
  .src.lit {
    border-color: var(--color-accent-dark);
    box-shadow: 0 0 0 1.5px var(--color-accent-dark);
  }
  .src.dim {
    opacity: 0.35;
  }
  input {
    padding: 12px;
    border: 1.5px solid var(--color-border);
    border-radius: 10px;
    background: var(--color-bg-surface);
    font: inherit;
    font-size: 16px;
    color: var(--color-text);
  }
  input:focus {
    outline: none;
    border-color: var(--color-accent-dark);
  }
  .error {
    margin: -4px 0 0;
    font-size: 13px;
    color: var(--color-error);
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .actions button {
    padding: 10px 16px;
    border-radius: 10px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }
  .secondary {
    border: 1px solid var(--color-border);
    background: none;
    color: var(--color-text-secondary);
  }
  .primary {
    border: none;
    background: var(--color-accent-dark);
    color: #fff;
  }
  .primary:disabled {
    opacity: 0.45;
    cursor: default;
  }
</style>
